import type { Color } from "@deck.gl/core";

import type { RiskPictureKind, RiskPictureLevel } from "./risk-picture-model";

/** Warning colors for this widget. Not the early-warning table palette. */
const LEVEL_COLOR: Record<RiskPictureLevel, Color> = {
  yellow: [255, 237, 0],
  orange: [255, 153, 0],
  red: [205, 0, 0],
};

export const riskPictureLevelColor = (level: RiskPictureLevel): Color => LEVEL_COLOR[level];

/** Part with no yellow/orange/red. Table bar and map polygon share it. */
export const RISK_PICTURE_NEUTRAL_COLOR: Color = [200, 200, 200];

/** Kansverwachting fill and lines. The SVG exports them opaque; in the design both are at 20%. */
const CHANCE_FILL: Record<RiskPictureLevel, { base: string; stripe: string }> = {
  yellow: { base: "#FFF8D8", stripe: "#F9E11E" },
  orange: { base: "#FCDECF", stripe: "#E17000" },
  red: { base: "#F6DBD9", stripe: "#D52B1E" },
};

const CHANCE_FILL_ALPHA = 0.2;

/** White. A transparent wash would pick up the gray cell or the map tiles. */
const CHANCE_PAPER = 255;

const flattenChannel = (channel: number, alpha: number): number =>
  Math.round(channel * alpha + CHANCE_PAPER * (1 - alpha));

/** Thickness of one colored band, in CSS pixels. The map keeps this while zooming. */
export const CHANCE_STRIPE_WIDTH_PX = 2;

/** Band thickness inside the stripe tile, in the tile's own units. */
const STRIPE_BAND_UNITS = 40;
const TILE_UNITS = 792;

/** Tile size that makes one band `CHANCE_STRIPE_WIDTH_PX` wide. */
export const CHANCE_STRIPE_TILE_PX = Math.round(
  (CHANCE_STRIPE_WIDTH_PX * TILE_UNITS) / STRIPE_BAND_UNITS,
);

const TILE_PX = CHANCE_STRIPE_TILE_PX;
const STRIPE_STEP = STRIPE_BAND_UNITS * Math.SQRT2;

const hexRgb = (hex: string): [number, number, number] => {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

/**
 * Chance wash as deck.gl RGBA. The 20% design opacity is flattened onto white
 * so the table bar and the map polygon paint the same color.
 */
export const chanceBaseColor = (level: RiskPictureLevel): Color => {
  const [red, green, blue] = hexRgb(CHANCE_FILL[level].base);
  return [
    flattenChannel(red, CHANCE_FILL_ALPHA),
    flattenChannel(green, CHANCE_FILL_ALPHA),
    flattenChannel(blue, CHANCE_FILL_ALPHA),
    255,
  ];
};

const stripeTile = (fill: string, opacity = CHANCE_FILL_ALPHA, mirrorY = false): string => {
  const rects: string[] = [];
  for (let copyY = -1; copyY <= 1; copyY += 1) {
    for (let copyX = -1; copyX <= 1; copyX += 1) {
      for (let index = 0; index < 16; index += 1) {
        const x = -452.417 + index * STRIPE_STEP + copyX * TILE_UNITS;
        const y = 452.68 - index * STRIPE_STEP + copyY * TILE_UNITS;
        rects.push(
          `<rect width="${STRIPE_BAND_UNITS}" height="1131" transform="translate(${x.toFixed(3)} ${y.toFixed(3)}) rotate(-45)"/>`,
        );
      }
    }
  }

  // The 3×3 copies overlap. Opacity sits on the group so overlaps don't stack
  // into darker stripes; per-rect fill-opacity would.
  const stripes = `<g fill="${fill}" opacity="${opacity}">${rects.join("")}</g>`;

  // The map texture is sampled with Y flipped, which reverses the diagonal.
  const body = mirrorY
    ? `<g transform="translate(0 ${TILE_UNITS}) scale(1 -1)">${stripes}</g>`
    : stripes;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${TILE_PX}" height="${TILE_PX}" viewBox="0 0 ${TILE_UNITS} ${TILE_UNITS}">${body}</svg>`;
};

const stripeDataUris = new Map<string, string>();

/** SVG stripe tile as an image URL for CSS backgrounds. */
export const chanceStripeAtlas = (level: RiskPictureLevel): string => {
  const stripe = CHANCE_FILL[level].stripe;
  const cached = stripeDataUris.get(stripe);
  if (cached) return cached;

  const uri = `data:image/svg+xml,${encodeURIComponent(stripeTile(stripe))}`;
  stripeDataUris.set(stripe, uri);
  return uri;
};

/**
 * Opaque white stripe tile for the deck.gl pattern mask. The texture is
 * premultiplied, so a 20% yellow tile would render dark; the mask only cuts
 * the shape and `chanceStripeColor` supplies the color and opacity.
 */
export const CHANCE_STRIPE_MASK_ATLAS = `data:image/svg+xml,${encodeURIComponent(
  stripeTile("#FFFFFF", 1, true),
)}`;

const hexByte = (channel: number): string => channel.toString(16).padStart(2, "0");

/**
 * Opaque fill for the map and popup badges. Chance badges paint `stripe` over
 * `fill`; issued badges are the solid warning color.
 */
export const riskPictureBadgeFill = (
  kind: RiskPictureKind,
  level: RiskPictureLevel | undefined,
): { fill: string; stripe?: string } => {
  if (kind === "chance" && level) {
    const { base, stripe } = CHANCE_FILL[level];
    return { fill: base, stripe };
  }
  if (level) {
    const [red, green, blue] = LEVEL_COLOR[level];
    return { fill: `#${hexByte(red)}${hexByte(green)}${hexByte(blue)}` };
  }
  return { fill: "#F3F3F3" };
};

/** Stripe color as deck.gl RGBA at the 20% design opacity. */
export const chanceStripeColor = (level: RiskPictureLevel): Color => {
  const [red, green, blue] = hexRgb(CHANCE_FILL[level].stripe);
  return [red, green, blue, Math.round(255 * CHANCE_FILL_ALPHA)];
};

/** Same wash as `chanceBaseColor`, plus the stripe tile. */
export const chanceBackground = (
  level: RiskPictureLevel,
): { base: string; stripes: string } => {
  const [red, green, blue] = chanceBaseColor(level);
  return {
    base: `rgb(${red} ${green} ${blue})`,
    stripes: `url("${chanceStripeAtlas(level)}")`,
  };
};
